import clsx from "clsx";
import { FC } from "react";

import { Icon } from "@tsmono/react/icons";

import styles from "./TagChip.module.css";

interface TagChipProps {
  label: string;
  // When true, render the chip with the "pending add" visual variant
  // (used inside the editor while there are uncommitted adds).
  isNew?: boolean;
  // Optional remove-callback. When provided, the chip renders a small
  // × button on the right (used inside the editor only). Mutually
  // exclusive with `onClick` — passing both would nest a button inside
  // a button.
  onRemove?: () => void;
  // Optional click-callback that makes the chip itself interactive
  // (rendered as a `<button>`). Used in the viewer header so clicking
  // any chip opens the edit dialog, matching the Edit/overflow pills.
  onClick?: () => void;
}

export const TagChip: FC<TagChipProps> = ({
  label,
  isNew,
  onRemove,
  onClick,
}) => {
  const className = clsx(
    styles.chip,
    isNew && styles.chipNew,
    onClick && styles.chipClickable,
    "text-size-smallest"
  );

  // The label lives in its own bounded element so a runaway-length
  // tag can't widen the whole chip — which would otherwise push the
  // inline Edit button off the right edge of the viewer header.
  const labelNode = <span className={styles.chipLabel}>{label}</span>;

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        title={label}
        aria-label={`Edit tags (${label})`}
        onClick={onClick}
      >
        {labelNode}
      </button>
    );
  }

  return (
    <span
      className={className}
      // Truncated labels stay discoverable on hover.
      title={label}
    >
      {labelNode}
      {onRemove && (
        <button
          type="button"
          className={styles.chipRemove}
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          <Icon name="close" />
        </button>
      )}
    </span>
  );
};
